import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useLocation } from 'react-router-dom';

import { Panel, SectionTitle, Icon, Text, Skeleton } from '../../../../components';
import type { IconName } from '../../../../components/Icon/Icon';
import { SparkleGlyph } from '../../../../layouts/MainLayout/components/SparkleGlyph';
import {
  CASINO_TECH_STATE,
  CASINO_TECH_STEP,
  COLORS,
  STATUS_COLORS,
  type DeploymentState,
  type DeploymentStepState,
} from '../../../../theme/tokens';
import type { DeploymentStatus, DeploymentStep, TechnicalDetails, TechnicalField } from '../../../../mock-data/types';
import { SectionSubtitle } from '../shared.styles';
import {
  Hero,
  HeroTop,
  StateDot,
  HeroHead,
  HeroTitle,
  HeroSub,
  StateIcon,
  ProgressRow,
  Bar,
  ProgressPct,
  Counts,
  CountChip,
  CountDot,
  StepsToggle,
  Chevron,
  Steps,
  Step,
  StepDot,
  StepLabel,
  StepMeta,
  Grid,
  Tile,
  TileLabel,
  TileRow,
  TileValue,
  Mono,
  TileActions,
  CopyBtn,
  LinkBtn,
  Empty,
  Body,
} from './TechnicalDetailsSection.styles';

/** Location hash the home Lumio card deep-links to, to scroll the deployment hero into view. */
const DEPLOYMENT_HASH = '#deployment';

export type TechnicalDetailsSectionProps = {
  details: TechnicalDetails;
  /** Gate the reveal until the brand list has loaded. */
  loading: boolean;
  /** False when no brand is selected yet, so we show a tidy note instead of empty tiles. */
  hasBrand: boolean;
  /** Page-level toast (copy confirmations + interpolation). */
  onToast: (message: string, placeholders?: Record<string, string | number>) => void;
};

/** Leading glyph per deployment state (headline copy is built reactively, see `stateCopy`). */
const STATE_ICON: Record<DeploymentState, IconName> = {
  live: 'check',
  deploying: 'sparkle',
  attention: 'warning',
};

/** Headline copy + leading glyph per deployment state, translated at render so it
 *  re-evaluates on language change. */
const stateCopy = (t: TFunction): Record<DeploymentState, { heading: string; sub: string; icon: IconName }> => ({
  live: { heading: t('Live'), sub: t('Your website is deployed and serving players.'), icon: STATE_ICON.live },
  deploying: { heading: t('Deploying'), sub: t('Lumio is building your content. This finishes on its own.'), icon: STATE_ICON.deploying },
  attention: { heading: t('Needs attention'), sub: t('A build step needs attention before your website can go live.'), icon: STATE_ICON.attention },
});

/** For steps with no timestamp, a one-word state instead; translated at render. */
const stepMeta = (t: TFunction): Record<DeploymentStepState, string> => ({
  completed: '',
  failed: '',
  active: t('Building'),
  pending: t('Queued'),
  skipped: t('Skipped'),
});

/** The count chips beside the progress bar, in pipeline order; zero-count ones drop out.
 *  Labels are translated at render so they re-evaluate on language change. */
const countChips = (counts: DeploymentStatus['counts'], t: TFunction): { id: DeploymentStepState; n: number; label: string }[] =>
  [
    { id: 'completed' as const, n: counts.completed, label: t('{{n}} done') },
    { id: 'active' as const, n: counts.active, label: t('{{n}} building') },
    { id: 'pending' as const, n: counts.pending, label: t('{{n}} queued') },
    { id: 'failed' as const, n: counts.failed, label: t('{{n}} failed') },
  ].filter((chip) => chip.n > 0);

/** Outbound-link glyph (open box + arrow) that flags the domain "Visit" affordance. */
const ExternalLinkIcon = (): React.ReactElement => (
  <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 5h5v5" />
    <path d="M19 5l-7 7" />
    <path d="M18 13.5v4A1.5 1.5 0 0 1 16.5 19h-10A1.5 1.5 0 0 1 5 17.5v-10A1.5 1.5 0 0 1 6.5 6h4" />
  </svg>
);

/** Chevron used by the build-steps toggle. */
const ChevronGlyph = (): React.ReactElement => (
  <svg width={12} height={12} viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** The verbose deployment-status hero: state + progress + counts + the full step list.
 *  Self-handles the home Lumio card's deep link: when the route carries the
 *  `#deployment` hash it scrolls itself into view and plays a one-shot arrival flash. */
const DeployHero = ({ deployment }: { deployment: DeploymentStatus }): React.ReactElement => {
  const { t } = useTranslation();
  const location = useLocation();
  const heroRef = React.useRef<HTMLDivElement>(null);

  const visual = CASINO_TECH_STATE[deployment.state];
  const copy = stateCopy(t)[deployment.state];
  const live = deployment.state === 'live';
  const inFlight = deployment.state === 'deploying' || deployment.state === 'attention';
  const hasSteps = deployment.steps.length > 0;

  // Deploy steps are collapsed by default; the owner expands them on demand. The
  // whole card toggles them (the labelled button stays the keyboard-accessible cue).
  const [expanded, setExpanded] = React.useState(false);
  const toggleSteps = React.useCallback(() => setExpanded((value) => !value), []);

  // Let the progress bar grow from 0 on first paint.
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);

  // Smooth-scroll + highlight when arrived via the home card's deep link.
  const [flash, setFlash] = React.useState(false);
  React.useEffect(() => {
    if (location.hash !== DEPLOYMENT_HASH) return undefined;
    // Defer two frames so the route-transition layout has settled before scrolling.
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => heroRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    });
    setFlash(true);
    const timer = window.setTimeout(() => setFlash(false), 1400);
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.clearTimeout(timer);
    };
  }, [location.hash, location.key]);

  const chips = countChips(deployment.counts, t);

  return (
    <Hero
      ref={heroRef}
      id="deployment"
      $glow={visual.glow}
      $flash={flash}
      $clickable={hasSteps}
      onClick={hasSteps ? toggleSteps : undefined}
    >
      <HeroTop>
        <StateDot $color={visual.accent} $pulse={inFlight} />
        <HeroHead>
          <HeroTitle text={copy.heading} tag="span" $color={visual.heading} />
          <HeroSub text={copy.sub} tag="span" />
        </HeroHead>
        <StateIcon>
          {deployment.state === 'deploying' ? <SparkleGlyph size={18} /> : <Icon name={copy.icon} size={18} color={visual.accent} />}
        </StateIcon>
      </HeroTop>

      {hasSteps ? (
        <>
          <ProgressRow
            role="progressbar"
            aria-label={t('Deployment progress')}
            aria-valuenow={deployment.progress}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <Bar value={deployment.progress} barColor={visual.accent} animate={mounted} />
            <ProgressPct text="{{pct}}%" placeholders={{ pct: deployment.progress }} tag="span" $color={visual.accent} />
          </ProgressRow>

          {chips.length > 0 ? (
            <Counts>
              {chips.map((chip) => (
                <CountChip key={chip.id}>
                  <CountDot $color={CASINO_TECH_STEP[chip.id].dot} />
                  <Text text={chip.label} placeholders={{ n: chip.n }} tag="span" />
                </CountChip>
              ))}
            </Counts>
          ) : null}

          <StepsToggle
            type="button"
            onClick={(event) => {
              // Stop the click bubbling to the card's own toggle, or the two cancel out.
              event.stopPropagation();
              toggleSteps();
            }}
            aria-expanded={expanded}
            aria-controls="deployment-steps"
          >
            {expanded ? <Text text="Hide deploy steps" tag="span" /> : <Text text="View deploy steps" tag="span" />}
            <Chevron $open={expanded}>
              <ChevronGlyph />
            </Chevron>
          </StepsToggle>

          {expanded ? (
            <Steps id="deployment-steps" role="list" aria-label={t('Deploy steps')}>
              {deployment.steps.map((step, index) => (
                <StepRow key={`${step.label}-${step.state}-${index}`} step={step} />
              ))}
            </Steps>
          ) : null}
        </>
      ) : live ? (
        <HeroSub text="Your casino is fully deployed." tag="span" />
      ) : (
        <HeroSub text="No deployment pipeline details are available for this casino yet." tag="span" />
      )}
    </Hero>
  );
};

/** Render-site translation for the verbose deployment-step labels. `technical.ts`
 *  humanises each real pipeline step (via the home `humanizeStep` helper) to a fixed,
 *  closed set of English chrome labels inside a non-reactive memo; translate that known
 *  vocabulary here so it re-localises on language change. An unknown / generically
 *  prettified step id (not in the fixed map) passes through untranslated. */
const stepLabel = (label: string, t: TFunction): string => {
  const labels: Record<string, string> = {
    CRM: t('CRM'),
    Lumio: t('Lumio'),
    'Email domain': t('Email domain'),
    Logos: t('Logos'),
    Notifications: t('Notifications'),
    'Tone of voice': t('Tone of voice'),
    'Welcome bonus': t('Welcome bonus'),
    'Homepage hero': t('Homepage hero'),
    'Knowledge base': t('Knowledge base'),
    Artwork: t('Artwork'),
    Theme: t('Theme'),
    Provisioning: t('Provisioning'),
  };
  return labels[label] ?? label;
};

const StepRow = ({ step }: { step: DeploymentStep }): React.ReactElement => {
  const { t } = useTranslation();
  const meta = step.at ?? stepMeta(t)[step.state];
  const label = stepLabel(step.label, t);
  return (
    <Step role="listitem">
      <StepDot $color={CASINO_TECH_STEP[step.state].dot} $pulse={step.state === 'active'} />
      <StepLabel text={label} tag="span" $color={CASINO_TECH_STEP[step.state].label} title={label} />
      {meta ? <StepMeta text={meta} tag="span" /> : null}
    </Step>
  );
};

/** Render-site translation for the field labels that `resolveTechnicalDetails`
 *  produces as stable English keys (it runs in a memo and cannot re-localise on
 *  language change, so the closed label vocabulary is translated here, reactively).
 *  Unknown ids fall back to the raw label, which `<Text>` still translates. */
const fieldLabel = (field: TechnicalField, t: TFunction): string => {
  const labels: Record<string, string> = {
    name: t('Casino name'),
    'casino-id': t('Casino ID'),
    domain: t('Domain'),
    'tenant-id': t('Tenant ID'),
    'owner-id': t('Owner ID'),
    created: t('Created'),
    deployed: t('Deployed'),
    updated: t('Last updated'),
    'next-invoice': t('Next invoice'),
    'pipeline-id': t('Pipeline ID'),
    'session-id': t('Deployment session'),
  };
  return labels[field.id] ?? field.label;
};

const FieldTile = ({
  field,
  copied,
  onCopy,
}: {
  field: TechnicalField;
  copied: boolean;
  onCopy: (field: TechnicalField) => void;
}): React.ReactElement => {
  const { t } = useTranslation();
  const label = fieldLabel(field, t);
  // The "Deployed" value is the one field value that is UI vocabulary ("In progress")
  // rather than a date/identifier; translate it here so it re-localises reactively.
  const value = field.value === 'In progress' ? t('In progress') : field.value;
  return (
    <Tile>
      <TileLabel>
        {field.icon ? <Icon name={field.icon} size={13} color={COLORS.textFaint} /> : null}
        <Text text={label} tag="span" />
      </TileLabel>
      <TileRow>
        {field.mono ? <Mono text={value} tag="span" title={value} /> : <TileValue text={value} tag="span" title={value} />}
        {field.href || field.copyable ? (
          <TileActions>
            {field.href ? (
              <LinkBtn href={field.href} target="_blank" rel="noopener noreferrer" aria-label={t('Open {{value}} in a new tab', { value: field.value })}>
                <ExternalLinkIcon />
              </LinkBtn>
            ) : null}
            {field.copyable ? (
              <CopyBtn type="button" aria-label={t('Copy {{label}}', { label })} onClick={() => onCopy(field)}>
                <Icon name={copied ? 'check' : 'copy'} size={13} color={copied ? STATUS_COLORS.positive : 'currentColor'} />
              </CopyBtn>
            ) : null}
          </TileActions>
        ) : null}
      </TileRow>
    </Tile>
  );
};

/**
 * "Technical details": the read-only panel at the foot of Casino Settings. Built
 * ENTIRELY from the selected brand's real `casino-instance` record (see
 * `resolveTechnicalDetails`): a verbose deployment-status hero (the home Lumio
 * card's content, expanded to every build step) plus a grid of copyable identifiers
 * and dates. Facts the API does not expose are omitted, never invented.
 */
export const TechnicalDetailsSection = ({ details, loading, hasBrand, onToast }: TechnicalDetailsSectionProps): React.ReactElement => {
  const { t } = useTranslation();
  // Which field just got copied, so its button briefly flips to a check (the toast
  // confirms too, but the in-place tick is the premium, self-contained cue).
  const [copiedId, setCopiedId] = React.useState<string | null>(null);
  const copiedTimer = React.useRef<number | undefined>(undefined);
  React.useEffect(() => () => window.clearTimeout(copiedTimer.current), []);

  const onCopy = React.useCallback(
    (field: TechnicalField) => {
      // `navigator.clipboard` is undefined in a non-secure context (e.g. LAN-IP dev),
      // so guard it explicitly, otherwise the optional chain would silently no-op
      // with no feedback. Always surface a result either way.
      if (!navigator.clipboard) {
        onToast(t("Couldn't copy to clipboard"));
        return;
      }
      void navigator.clipboard
        .writeText(field.value)
        .then(() => {
          onToast(t('{{label}} copied to clipboard'), { label: fieldLabel(field, t) });
          setCopiedId(field.id);
          window.clearTimeout(copiedTimer.current);
          copiedTimer.current = window.setTimeout(() => setCopiedId(null), 1200);
        })
        .catch(() => onToast(t("Couldn't copy to clipboard")));
    },
    [onToast, t],
  );

  return (
    <Panel>
      <SectionTitle title={t('Technical details')} icon="info" />
      <SectionSubtitle
        text="A behind-the-scenes look at your website: its identifiers, web address, build dates and the live status of its content deployment."
        tag="span"
      />

      {loading ? (
        <Skeleton block height={320} radius={16} />
      ) : !hasBrand ? (
        <Empty text="Select a casino to see its technical details." tag="span" />
      ) : (
        <Body>
          <DeployHero deployment={details.deployment} />

          <Grid>
            {details.fields.map((field) => (
              <FieldTile key={field.id} field={field} copied={copiedId === field.id} onCopy={onCopy} />
            ))}
          </Grid>
        </Body>
      )}
    </Panel>
  );
};

export default TechnicalDetailsSection;
